import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useTranslations } from "use-intl";
import { useEffect, useState, useTransition } from "react";
import { toast } from "sonner";
import { Download, LogOut, Monitor, Moon, Sun, Trash2 } from "lucide-react";
import { deleteAccount, updateProfile } from "@/lib/actions";
import { LanguageSwitch } from "@/components/app/language-switch";
import { useTheme, type Theme } from "@/components/theme";
import { Section } from "@/components/app/page-header";
import { Stepper } from "@/components/app/stepper";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { signOut } from "@/lib/auth-client";
import { clearOfflineData } from "@/lib/offline";
import { disablePush, enablePush, pushEnabled, pushSupport } from "@/lib/push";

type Settings = { beginnerMode: boolean; weeklyGoal: number; soundEnabled: boolean; vibrationEnabled: boolean };

export function SettingsForm({ initial }: { initial: Settings }) {
  const t = useTranslations("profile");
  const { theme, setTheme } = useTheme();
  const [s, setS] = useState(initial);
  const [, start] = useTransition();

  const change = (patch: Partial<Settings>) => {
    setS((prev) => ({ ...prev, ...patch }));
    start(() => updateProfile(patch));
  };

  return (
    <>
      <Section title={t("language")}>
        <Card className="py-3">
          <CardContent className="space-y-3 px-4">
            <div className="flex items-center justify-between gap-2">
              <Label>{t("language")}</Label>
              <LanguageSwitch />
            </div>
            <Separator />
            <div className="flex items-center justify-between gap-2">
              <Label>{t("theme")}</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                value={theme}
                onValueChange={(v) => v && setTheme(v as Theme)}
              >
                <ToggleGroupItem value="light" aria-label={t("themes.light")}>
                  <Sun />
                </ToggleGroupItem>
                <ToggleGroupItem value="dark" aria-label={t("themes.dark")}>
                  <Moon />
                </ToggleGroupItem>
                <ToggleGroupItem value="system" aria-label={t("themes.system")}>
                  <Monitor />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardContent>
        </Card>
      </Section>

      <Section title={t("training")}>
        <Card className="py-3">
          <CardContent className="space-y-3 px-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="beginner">{t("beginnerMode")}</Label>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("beginnerModeHelp")}</p>
              </div>
              <Switch id="beginner" checked={s.beginnerMode} onCheckedChange={(v) => change({ beginnerMode: v })} />
            </div>
            <Separator />
            <Stepper
              label={t("weeklyGoal")}
              value={s.weeklyGoal}
              min={1}
              max={7}
              size="sm"
              onChange={(v) => change({ weeklyGoal: v ?? 3 })}
            />
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="sound">{t("sound")}</Label>
              <Switch id="sound" checked={s.soundEnabled} onCheckedChange={(v) => change({ soundEnabled: v })} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="vibration">{t("vibration")}</Label>
              <Switch
                id="vibration"
                checked={s.vibrationEnabled}
                onCheckedChange={(v) => change({ vibrationEnabled: v })}
              />
            </div>
            <Separator />
            <RestNotificationSetting />
          </CardContent>
        </Card>
      </Section>
    </>
  );
}

/** Push notification when the rest ends in the background – per device, so not part of the profile. */
function RestNotificationSetting() {
  const t = useTranslations("profile");
  const tc = useTranslations("common");
  const support = pushSupport();
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void pushEnabled().then(setOn, () => setOn(false));
  }, []);

  async function toggle(enable: boolean) {
    setBusy(true);
    try {
      if (enable) {
        const granted = await enablePush();
        setOn(granted);
        if (!granted) toast.error(t("notificationsDenied"));
      } else {
        await disablePush();
        setOn(false);
      }
    } catch {
      toast.error(tc("error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <Label htmlFor="rest-push">{t("restNotification")}</Label>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {support === "supported" ? t("restNotificationHelp") : t(`restNotificationNo.${support}`)}
        </p>
      </div>
      <Switch id="rest-push" checked={on} disabled={support !== "supported" || busy} onCheckedChange={toggle} />
    </div>
  );
}

/** Downloads straight from the API (same origin, the session cookie is sent along). */
export function DataExport() {
  const t = useTranslations("profile");
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <Section title={t("data")}>
      <p className="-mt-1 mb-2 text-xs text-muted-foreground">{t("dataHelp")}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Button asChild variant="outline" size="lg">
          <a href={`/api/me/export?format=csv&tz=${encodeURIComponent(tz)}`} download>
            <Download /> {t("exportCsv")}
          </a>
        </Button>
        <Button asChild variant="outline" size="lg">
          <a href="/api/me/export?format=json" download>
            <Download /> {t("exportJson")}
          </a>
        </Button>
      </div>
    </Section>
  );
}

export function AccountActions() {
  const t = useTranslations("profile");
  const ta = useTranslations("auth");
  const tc = useTranslations("common");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [pending, start] = useTransition();
  const leave = () => {
    void clearOfflineData();
    qc.clear();
    navigate("/", { replace: true });
  };
  return (
    <Section title={t("account")}>
      <div className="space-y-2">
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={async () => {
            // Signed out, this device gets no more notifications.
            await disablePush().catch(() => {});
            await signOut();
            leave();
          }}
        >
          <LogOut /> {ta("logout")}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" className="w-full text-destructive">
              <Trash2 /> {t("deleteAccount")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{tc("deleteConfirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>{t("deleteAccountText")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{tc("cancel")}</AlertDialogCancel>
              <AlertDialogAction variant="destructive" disabled={pending} onClick={() =>
                start(async () => {
                  await disablePush().catch(() => {});
                  await deleteAccount();
                  await signOut().catch(() => {});
                  leave();
                })
              }>
                {t("deleteAccount")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Section>
  );
}
